import { Alert } from '@linktr.ee/arbor/components/Alert';
import { Text } from '@linktr.ee/arbor/components/Text';
import { InfoIcon } from '@linktr.ee/arbor/icons';
import { Turnstile } from '@marsidev/react-turnstile';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import AuthForm from '@shared/components/AuthForm';
import Button from '@shared/components/Button';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import { usePublishScreenViewedEvent } from '@shared/utils/analytics/hooks';
import { validateEmail } from '@shared/utils/validation';
import { translateValidationError } from '@shared/utils/validationMessages';
import bannerLoginImage from '../universal-login/images/banner-login-desktop.webp';

export type ForgotUsernameScreenMode = 'form' | 'sent' | 'technicalError';
export type ForgotUsernameFormError = 'userNotFound' | 'captcha';

interface ForgotUsernameScreenProps {
  screen: ForgotUsernameScreenMode;
  initialEmail?: string;
  formError?: ForgotUsernameFormError;
  turnstileSiteKey: string;
  onSubmit: (email: string, captchaToken: string) => Promise<void>;
  onRetry: () => void | Promise<void>;
  onBackToLogin: () => void | Promise<void>;
  resetError: () => void;
}

const SUPPORT_URL =
  'https://linktr.ee/help/en/articles/6608177-i-can-t-log-in-to-my-account-what-should-i-do';
const FORGOT_USERNAME_SCREEN_TITLE = 'Log In - Forgot Username';
const recoveryLayoutProps = {
  variant: 'banner',
  bannerImageDesktop: bannerLoginImage,
} as const;

const obscureEmailString = (email: string): string => {
  const atIndex = email.indexOf('@');

  if (atIndex === -1) {
    return email;
  }

  const username = email.substring(0, atIndex);
  const domain = email.substring(atIndex);

  if (username.length <= 1) {
    return `${username}***${domain}`;
  }

  if (username.length <= 3) {
    return `${username[0]}***${domain}`;
  }

  return `${username[0]}***${username[username.length - 1]}${domain}`;
};

const TurnstileTerms = () => {
  const { t } = useTranslation();

  const privacyLink = (
    <a
      className="underline"
      href="https://www.cloudflare.com/privacypolicy/"
      target="_blank"
      rel="noopener noreferrer"
    >
      {t('Cloudflare Privacy Policy')}
    </a>
  );

  const termsLink = (
    <a
      className="underline"
      href="https://www.cloudflare.com/website-terms/"
      target="_blank"
      rel="noopener noreferrer"
    >
      {t('Terms of Service')}
    </a>
  );

  return (
    <Text
      as="p"
      variant="caption-large-regular"
      className="text-center text-gray-500"
    >
      <Trans
        i18nKey="This site is protected by Cloudflare Turnstile and the <privacyLink>Cloudflare Privacy Policy</privacyLink> and <termsLink>Terms of Service</termsLink> apply."
        components={{
          privacyLink,
          termsLink,
        }}
      />
    </Text>
  );
};

const getFormErrorCopy = (
  formError: ForgotUsernameFormError,
  t: ReturnType<typeof useTranslation>['t']
) => {
  switch (formError) {
    case 'userNotFound':
      return {
        title: t("Let's double-check your email address."),
        message: (
          <Text asChild variant="body-small-medium" className="text-accent">
            <a href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
              {t("Still can't log in?")}
            </a>
          </Text>
        ),
      };
    case 'captcha':
      return {
        title: t("We couldn't verify you're human."),
        message: t('Please try again.'),
      };
  }
};

const ForgotUsernameScreen: React.FC<ForgotUsernameScreenProps> = ({
  screen,
  initialEmail = '',
  formError,
  turnstileSiteKey,
  onSubmit,
  onRetry,
  onBackToLogin,
  resetError,
}) => {
  const { t } = useTranslation();
  usePublishScreenViewedEvent(FORGOT_USERNAME_SCREEN_TITLE);
  const [email, setEmail] = useState(initialEmail);
  const [emailError, setEmailError] = useState<string | undefined>();
  const [captchaError, setCaptchaError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isReturningToLogin, setIsReturningToLogin] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  const handleEmailChange = (value: string) => {
    resetError();
    setEmailError(undefined);
    setCaptchaError(false);
    setEmail(value);
  };

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
  };

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    const validation = validateEmail(trimmedEmail);

    if (!validation.isValid) {
      setEmailError(validation.error);
      return;
    }

    resetError();
    setCaptchaError(false);
    setIsSubmitting(true);

    try {
      let captchaToken: string | undefined;

      try {
        captchaToken = await turnstileRef.current?.getResponsePromise();
      } catch {
        setCaptchaError(true);
        resetTurnstile();
        return;
      }

      if (!captchaToken) {
        setCaptchaError(true);
        resetTurnstile();
        return;
      }

      await onSubmit(trimmedEmail, captchaToken);
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = async () => {
    setIsRetrying(true);

    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleBackToLogin = async () => {
    setIsReturningToLogin(true);

    try {
      await onBackToLogin();
    } finally {
      setIsReturningToLogin(false);
    }
  };

  if (screen === 'form') {
    const localizedValidationError = translateValidationError(t, emailError);
    const displayFormError = captchaError ? 'captcha' : formError;
    const formErrorCopy = displayFormError
      ? getFormErrorCopy(displayFormError, t)
      : undefined;

    return (
      <Layout
        {...recoveryLayoutProps}
        heading={t('Forgot username?')}
        subHeading={t(
          "Enter the email address you used to create your account and we'll send you an email with your username(s)."
        )}
      >
        <div className="space-y-6">
          {formErrorCopy && (
            <Alert
              role="alert"
              status="error"
              icon={<InfoIcon />}
              title={formErrorCopy.title}
              message={formErrorCopy.message}
              className="!border-0 !rounded-lg text-left"
            />
          )}

          <AuthForm>
            <InputText
              label={t('Email')}
              name="email"
              type="email"
              value={email}
              maxLength={100}
              autoCapitalize="none"
              autoComplete="email"
              disabled={isSubmitting}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleEmailChange(event.target.value);
              }}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                  event.key === 'Enter' &&
                  email.length > 0 &&
                  !isSubmitting
                ) {
                  event.preventDefault();
                  void handleSubmit();
                }
              }}
              errorMessage={localizedValidationError}
              state={
                localizedValidationError || displayFormError
                  ? 'error'
                  : 'default'
              }
            />
            <span role="status" className="sr-only">
              {localizedValidationError}
            </span>

            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onExpire={resetTurnstile}
                options={{
                  appearance: 'interaction-only',
                }}
              />
            </div>

            <Button
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                void handleSubmit();
              }}
              variant="primary"
              disabled={email.trim().length === 0 || isSubmitting}
              label={t('Send email')}
              state={isSubmitting ? 'loading' : 'default'}
            />
          </AuthForm>

          <div className="flex justify-center">
            <Button
              variant="link"
              fullWidth={false}
              label={t('Back to log in')}
              onClick={() => {
                void handleBackToLogin();
              }}
              state={isReturningToLogin ? 'loading' : 'default'}
            />
          </div>

          <TurnstileTerms />
        </div>
      </Layout>
    );
  }

  if (screen === 'sent') {
    return (
      <Layout
        {...recoveryLayoutProps}
        heading={t('Check your email')}
        subHeading={t(
          'We just sent an email with your username(s) to {{email}}.',
          { email: obscureEmailString(initialEmail) }
        )}
      >
        <Button
          onClick={() => {
            void handleBackToLogin();
          }}
          variant="primary"
          label={t('Back to log in')}
          state={isReturningToLogin ? 'loading' : 'default'}
        />
      </Layout>
    );
  }

  return (
    <Layout
      {...recoveryLayoutProps}
      heading={t('Something went wrong')}
      subHeading={t('Please refresh the page and try again.')}
    >
      <div className="space-y-4">
        <Button
          onClick={() => {
            void handleRetry();
          }}
          variant="primary"
          label={t('Try again')}
          state={isRetrying ? 'loading' : 'default'}
        />
        <Button
          onClick={() => {
            void handleBackToLogin();
          }}
          variant="outline"
          label={t('Back to log in')}
          state={isReturningToLogin ? 'loading' : 'default'}
        />
      </div>
    </Layout>
  );
};

export default ForgotUsernameScreen;
